/** What a Tab does to a note's text: the span it replaces, what goes there, and the selection after. */
export interface Indent {
  from: number;
  to: number;
  insert: string;
  start: number;
  end: number;
}

const STEP = '  ';

/** How much of [line]'s lead one Shift+Tab takes: a tab, or up to one step of spaces. */
function leadOf(line: string): number {
  if (line.startsWith('\t')) return 1;
  let n = 0;
  while (n < STEP.length && line[n] === ' ') n++;
  return n;
}

/**
 * Tab at a caret puts one step of spaces there; over a selection, or with Shift, it moves each line
 * the selection touches a step in or out. Spaces rather than a tab character, so a nested list reads
 * as nested in the note's Markdown and not as code.
 */
export function indent(text: string, start: number, end: number, out: boolean): Indent | null {
  if (!out && start === end) return { from: start, to: end, insert: STEP, start: start + STEP.length, end: end + STEP.length };

  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  // A selection ending just past a line break does not take in the line after it.
  const lastAt = end > start && text[end - 1] === '\n' ? end - 1 : end;
  const breakAt = text.indexOf('\n', lastAt);
  const blockEnd = breakAt === -1 ? text.length : breakAt;
  const lines = text.slice(lineStart, blockEnd).split('\n');

  let moved = 0;
  let firstMoved = 0;
  const next = lines.map((line, i) => {
    if (out) {
      const lead = leadOf(line);
      if (i === 0) firstMoved = -Math.min(lead, start - lineStart);
      moved -= lead;
      return line.slice(lead);
    }
    if (line.length === 0 && lines.length > 1) return line;
    if (i === 0) firstMoved = STEP.length;
    moved += STEP.length;
    return STEP + line;
  });
  if (moved === 0) return null;
  return {
    from: lineStart,
    to: blockEnd,
    insert: next.join('\n'),
    start: Math.max(lineStart, start + firstMoved),
    end: start === end ? Math.max(lineStart, start + firstMoved) : end + moved,
  };
}
